import React, { useState } from 'react';
import { trackWhatsAppClick } from '../services/analyticsService';
import { createPropertyLead } from '../utils/createPropertyLead';
import { showNotification } from './Notification';
import CustomDatePicker from './CustomDatePicker';
import { buildPropertyPath } from '../utils/seo/urlHelpers';

const MAX_NOME_LEN = 35;
const MAX_EMAIL_LEN = 50;
const MAX_MSG_LEN = 100;

function formatBrPhoneInput(value) {
  const numbers = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 11);
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  }
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
}

function phoneDigitsOk(formatted) {
  const d = String(formatted || '').replace(/\D/g, '');
  return d.length === 10 || d.length === 11;
}

function isValidEmailOptional(value) {
  const s = String(value || '').trim();
  if (!s) return true;
  if (s.length > MAX_EMAIL_LEN) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

const emptyInfoForm = { nome: '', email: '', telefone: '', mensagem: '' };
const emptyVisitForm = { nome: '', email: '', telefone: '', visitDate: '', visitTime: '', mensagem: '' };

export default function PropertyContactActions({ property, settings }) {
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [infoForm, setInfoForm] = useState(emptyInfoForm);
  const [visitForm, setVisitForm] = useState(emptyVisitForm);
  const [submitting, setSubmitting] = useState(null);

  const getWhatsappNumber = () => {
    const raw = settings?.contactWhatsapp || settings?.contactPhone || '5511999999999';
    return String(raw).replace(/\D/g, '');
  };

  const getPropertyUrl = () => {
    if (!property?.id) return window.location.href;
    return `${window.location.origin}${buildPropertyPath(property)}`;
  };

  const openPropertyWhatsApp = () => {
    if (!property) return;
    trackWhatsAppClick({ source: 'property_contact_whatsapp', itemId: property.id, itemType: 'property' });
    const url = getPropertyUrl();
    const text = `Olá! Tenho interesse neste imóvel.\n\n${property.title} (${property.code})\n${url}`;
    const whatsappUrl = `https://wa.me/${getWhatsappNumber()}?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const togglePanel = (panel) => {
    setExpandedPanel((prev) => {
      const next = prev === panel ? null : panel;
      if (next === 'info') {
        setInfoForm((current) => ({
          ...current,
          mensagem: (current.mensagem || (property?.code
            ? `Olá, gostaria de mais informações sobre o imóvel ${property.code}`
            : 'Olá, gostaria de mais informações sobre este imóvel.')).slice(0, MAX_MSG_LEN)
        }));
      }
      return next;
    });
  };

  const fieldHeightClass = 'h-[42px] py-0 box-border';

  const datePickerTriggerClassName =
    `${fieldHeightClass} px-3 text-sm border-gray-300 bg-white focus:outline-none focus:border-[color:var(--primary-color,#3B82F6)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary-color,#3B82F6)_22%,transparent)]`;

  const textareaClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm transition-[border-color,box-shadow] focus:outline-none focus:border-[color:var(--primary-color,#3B82F6)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary-color,#3B82F6)_22%,transparent)] resize-none';

  const inputClass =
    `w-full border border-gray-300 rounded-lg px-3 text-sm ${fieldHeightClass} transition-[border-color,box-shadow] focus:outline-none focus:border-[color:var(--primary-color,#3B82F6)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary-color,#3B82F6)_22%,transparent)]`;

  const validateCommonFields = (form) => {
    const nomeTrim = form.nome.trim().slice(0, MAX_NOME_LEN);
    if (!nomeTrim) return 'Informe seu nome.';
    if (!phoneDigitsOk(form.telefone)) return 'Informe um telefone válido com DDD (10 ou 11 dígitos).';
    if (!isValidEmailOptional(form.email)) return 'Informe um e-mail válido ou deixe o campo em branco.';
    return null;
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    const error = validateCommonFields(infoForm);
    if (error) {
      showNotification(error, 'error');
      return;
    }

    setSubmitting('info');
    try {
      await createPropertyLead({
        name: infoForm.nome.trim(),
        phone: infoForm.telefone.trim(),
        email: infoForm.email.trim(),
        message: infoForm.mensagem.trim().slice(0, MAX_MSG_LEN),
        intent: 'info',
        property
      });
      setInfoForm(emptyInfoForm);
      setExpandedPanel(null);
      showNotification('Solicitação enviada! Em breve entraremos em contato.', 'success');
    } catch (err) {
      console.error(err);
      showNotification('Não foi possível enviar. Tente novamente.', 'error');
    } finally {
      setSubmitting(null);
    }
  };

  const handleVisitSubmit = async (e) => {
    e.preventDefault();
    const error = validateCommonFields(visitForm);
    if (error) {
      showNotification(error, 'error');
      return;
    }
    if (!visitForm.visitDate) {
      showNotification('Informe a data preferida para a visita.', 'error');
      return;
    }

    setSubmitting('visit');
    try {
      await createPropertyLead({
        name: visitForm.nome.trim(),
        phone: visitForm.telefone.trim(),
        email: visitForm.email.trim(),
        message: visitForm.mensagem.trim().slice(0, MAX_MSG_LEN),
        intent: 'visit',
        visitDate: visitForm.visitDate,
        visitTime: visitForm.visitTime,
        property
      });
      setVisitForm(emptyVisitForm);
      setExpandedPanel(null);
      showNotification('Pedido de visita enviado! Em breve entraremos em contato.', 'success');
    } catch (err) {
      console.error(err);
      showNotification('Não foi possível enviar. Tente novamente.', 'error');
    } finally {
      setSubmitting(null);
    }
  };

  const outlinedButtonClass =
    'flex items-center justify-center gap-2 w-full border border-gray-800 bg-white text-gray-900 py-3 px-4 rounded-full transition-colors hover:bg-gray-50 text-sm font-medium';

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={openPropertyWhatsApp}
        className="flex items-center justify-center gap-2 w-full border border-white bg-[#25D366] text-white py-3 px-4 rounded-full transition-colors hover:bg-[#20BD5A] text-sm font-medium"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        Converse pelo Whatsapp
      </button>

      <div className="relative text-center py-1">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative">
          <span className="px-4 text-sm text-gray-500 bg-white">OU</span>
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => togglePanel('info')}
          className="flex items-center justify-center w-full py-3 px-4 rounded-full transition-opacity hover:opacity-90 text-sm font-medium"
          style={{
            backgroundColor: 'var(--primary-color, #3B82F6)',
            color: 'var(--secondary-color, #ffffff)',
          }}
          aria-expanded={expandedPanel === 'info'}
        >
          Quero mais informações
        </button>

        {expandedPanel === 'info' && (
          <form onSubmit={handleInfoSubmit} className="mt-3 space-y-3 border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome <span className="text-red-600">*</span></label>
              <input
                type="text"
                required
                maxLength={MAX_NOME_LEN}
                value={infoForm.nome}
                onChange={(e) => setInfoForm((prev) => ({ ...prev, nome: e.target.value.slice(0, MAX_NOME_LEN) }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={infoForm.email}
                onChange={(e) => setInfoForm((prev) => ({ ...prev, email: e.target.value.slice(0, MAX_EMAIL_LEN) }))}
                maxLength={MAX_EMAIL_LEN}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone <span className="text-red-600">*</span></label>
              <input
                type="tel"
                required
                value={infoForm.telefone}
                onChange={(e) => setInfoForm((prev) => ({ ...prev, telefone: formatBrPhoneInput(e.target.value) }))}
                placeholder="(00) 00000-0000"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem</label>
              <textarea
                rows="3"
                maxLength={MAX_MSG_LEN}
                value={infoForm.mensagem}
                onChange={(e) => setInfoForm((prev) => ({ ...prev, mensagem: e.target.value.slice(0, MAX_MSG_LEN) }))}
                className={textareaClass}
              />
            </div>
            <button
              type="submit"
              disabled={submitting === 'info'}
              className="w-full py-3 rounded-full transition-opacity hover:opacity-90 text-sm font-medium disabled:opacity-60"
              style={{
                backgroundColor: 'var(--primary-color, #3B82F6)',
                color: 'var(--secondary-color, #ffffff)',
              }}
            >
              {submitting === 'info' ? 'Enviando…' : 'Enviar solicitação'}
            </button>
          </form>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => togglePanel('visit')}
          className={outlinedButtonClass}
          aria-expanded={expandedPanel === 'visit'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
          Agendar visita
        </button>

        {expandedPanel === 'visit' && (
          <form onSubmit={handleVisitSubmit} className="mt-3 space-y-3 border border-gray-200 rounded-xl p-4 bg-gray-50 overflow-visible">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome <span className="text-red-600">*</span></label>
              <input
                type="text"
                required
                maxLength={MAX_NOME_LEN}
                value={visitForm.nome}
                onChange={(e) => setVisitForm((prev) => ({ ...prev, nome: e.target.value.slice(0, MAX_NOME_LEN) }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={visitForm.email}
                onChange={(e) => setVisitForm((prev) => ({ ...prev, email: e.target.value.slice(0, MAX_EMAIL_LEN) }))}
                maxLength={MAX_EMAIL_LEN}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone <span className="text-red-600">*</span></label>
              <input
                type="tel"
                required
                value={visitForm.telefone}
                onChange={(e) => setVisitForm((prev) => ({ ...prev, telefone: formatBrPhoneInput(e.target.value) }))}
                placeholder="(00) 00000-0000"
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data preferida <span className="text-red-600">*</span></label>
                <CustomDatePicker
                  value={visitForm.visitDate}
                  onChange={(val) => setVisitForm((prev) => ({ ...prev, visitDate: val }))}
                  placeholder="dd/mm/aaaa"
                  minDate={new Date().toISOString().split('T')[0]}
                  disabled={submitting === 'visit'}
                  compact
                  usePortal
                  triggerClassName={datePickerTriggerClassName}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Horário</label>
                <input
                  type="time"
                  value={visitForm.visitTime}
                  onChange={(e) => setVisitForm((prev) => ({ ...prev, visitTime: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  rows="2"
                  maxLength={MAX_MSG_LEN}
                  value={visitForm.mensagem}
                  onChange={(e) => setVisitForm((prev) => ({ ...prev, mensagem: e.target.value.slice(0, MAX_MSG_LEN) }))}
                  placeholder="Alguma preferência para a visita?"
                className={textareaClass}
                />
            </div>
            <button
              type="submit"
              disabled={submitting === 'visit'}
              className="w-full py-3 rounded-full border border-gray-800 bg-white text-gray-900 transition-colors hover:bg-gray-50 text-sm font-medium disabled:opacity-60"
            >
              {submitting === 'visit' ? 'Enviando…' : 'Solicitar visita'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
